import React from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";

const DerivativeWorks = () => {
  const seoData = {
    title: "Derivative Works Guidelines - Livneu | Fan Art & Content Creation Rules",
    description:
      "Read Livneu's comprehensive guidelines for creating derivative works, fan art, clips, and other fan-created content. Learn what's allowed and how to properly credit our talents and content.",
    image: "https://livneu.levelstair.com/ShareImage.png",
    url: "https://livneu.levelstair.com/derivative-works",
    keywords:
      "derivative works, fan art guidelines, content creation rules, Livneu guidelines, fan content policy, clip guidelines",
  };

  const allowedContent = [
    "Fan art and illustrations featuring our talents",
    "Music covers and remixes of official songs",
    "Clip compilations and highlight videos", 
    "Memes and reaction content",
    "Cosplay and character interpretations",
    "Fan fiction and creative writing",
    "Game modifications featuring our characters",
    "Educational or analytical content"
  ];

  const prohibitedContent = [
    "Content falsely represented as official",
    "Content contradictory to public order and morality",
    "Content involving specific ideologies, politics, or controversial beliefs",
    "Content that damages our image or that of our talents",
    "Content that infringes on third-party rights",
    "Commercial use without explicit permission",
    "Content containing hate speech or harassment",
    "Adult or inappropriate content involving our characters"
  ];

  const clipGuidelines = [
    {
      title: "Source Attribution Required",
      description: "All clips must include a direct link to the original stream or video in the description, along with the source title and talent name."
    },
    {
      title: "Wait for VOD Publication",
      description: "Do not upload clips before the original stream ends and the VOD (Video on Demand) becomes publicly available."
    },
    {
      title: "Respect Content Guidelines",
      description: "Ensure clip thumbnails and titles are appropriate and do not misrepresent the original content or context."
    },
    {
      title: "No Member-Only Content",
      description: "Never clip or share content from paid memberships, private streams, or other exclusive access content."
    }
  ];

  const musicGuidelines = [
    {
      title: "Credit Requirements",
      description: "When creating covers or remixes, use the format: 'Song: [Song Title], Artist: Livneu [Talent Name]'"
    },
    {
      title: "Platform Monetization",
      description: "You may use platform monetization features (YouTube Partner Program, etc.) for musical derivative works, but cannot register them with Content ID systems."
    },
    {
      title: "Commercial Restrictions", 
      description: "Physical sales (CDs, vinyl) and commercial music streaming distribution require separate permission and licensing agreements."
    },
    {
      title: "DJ Performances",
      description: "Live DJ performances using our music are allowed, but ensure proper copyright fees are paid where required."
    }
  ];

  return (
    <HelmetProvider>
      <div className="min-h-screen">
        <SEOHead seoData={seoData} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-derivatives">
          {/* Kawaii Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-pastel-purple/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-pastel-blue/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s" }}></div>
            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-kawaii-mint/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: "4s" }}></div>
          </div>

          <div className="container mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-7xl font-title text-text-accent mb-6 leading-tight kawaii-glow">
                Derivative Works Guidelines
              </h1>
              <motion.p
                className="text-xl md:text-2xl text-text-secondary mb-8 leading-relaxed font-body"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Guidelines for creating fan art, clips, and other derivative works featuring Livneu content and talents
              </motion.p>
              <p className="text-sm text-text-secondary/60 mt-6">
                Last updated: January 2025
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-6">
          <div className="container mx-auto max-w-6xl">
          
          {/* Introduction */}
          <motion.section 
            className="mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gray-900/50 rounded-2xl p-8 md:p-12 border border-gray-700">
              <h2 className="text-3xl md:text-4xl font-bold text-purple-400 mb-6">Introduction</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                LEVELSTAIR (Pvt) Ltd., operating Livneu, has established these comprehensive guidelines 
                to support and encourage fan creativity while protecting the rights and interests of our talents 
                and intellectual property.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We believe in fostering a creative community where fans can express their appreciation and 
                creativity through various forms of derivative works. These guidelines ensure that fan-created 
                content can be enjoyed safely and legally by everyone.
              </p>
              <div className="bg-green-500/10 rounded-xl p-6 border border-green-500/30">
                <p className="text-green-300 font-semibold text-lg">
                  ✅ Good News: When creating derivative works that comply with these guidelines, 
                  you do NOT need to contact us for permission beforehand.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Definition of Derivative Works */}
          <motion.section 
            className="mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gray-900/50 rounded-2xl p-8 md:p-12 border border-purple-500/30">
              <h2 className="text-3xl md:text-4xl font-bold text-purple-300 mb-6">Definition of Derivative Works</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We consider derivative works to be <strong className="text-purple-400">original creations born of fans' 
                ideas and creativity</strong>, based on content created by Livneu and our talents.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Livneu will not exercise copyright claims against works that we classify as derivative 
                works, provided they comply with these guidelines.
              </p>
              <div className="bg-blue-500/10 rounded-xl p-6 border border-blue-500/30 mb-6">
                <h3 className="text-xl font-bold text-blue-300 mb-4">Examples of Accepted Derivative Works:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {allowedContent.map((content, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700">{content}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-purple-500/10 rounded-xl p-6 border border-purple-500/30">
                <p className="text-purple-300 font-semibold">
                  ⚠️ Important: Simply using our content "as is" or with minimal modifications lacking creative 
                  input is NOT considered derivative work and does not fall within the scope of these guidelines.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Our Requests - General */}
          <motion.section 
            className="mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gray-900/50 rounded-2xl p-8 md:p-12 border border-red-500/30">
              <h2 className="text-3xl md:text-4xl font-bold text-red-300 mb-8">General Guidelines & Requirements</h2>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-black mb-4">✅ What We Encourage</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Be mindful and respectful of our talents - create content they would appreciate</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Limit creation to fan or hobby level (non-commercial purposes)</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Comply with all applicable laws and platform terms of service</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Clearly indicate that fan works are unofficial creations</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-black mb-4">❌ Prohibited Content</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {prohibitedContent.map((content, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-3 h-3 bg-red-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                        <span className="text-gray-700">{content}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Clip Guidelines */}
          <motion.section 
            className="mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gray-900/50 rounded-2xl p-8 md:p-12 border border-blue-500/30">
              <h2 className="text-3xl md:text-4xl font-bold text-blue-300 mb-8">Guidelines for Clips & Video Content</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                We encourage fans to create and share clips of our streams and content to help spread awareness 
                and entertainment. However, please follow these specific guidelines for clip creation:
              </p>
              
              <div className="space-y-6">
                {clipGuidelines.map((guideline, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="bg-gray-800/50 rounded-xl p-6 border border-blue-500/30"
                  >
                    <h3 className="text-xl font-bold text-blue-300 mb-3">{guideline.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{guideline.description}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 bg-cyan-500/10 rounded-xl p-6 border border-gray-700">
                <h3 className="text-xl font-bold text-purple-400 mb-4">Monetization for Clips</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We allow monetization of clips through platform partner programs (YouTube, etc.) provided you:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">Follow all guidelines and platform terms</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">Never register clips with Content ID or automated systems</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">Respect third-party copyrights (background music, games, etc.)</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Music Guidelines */}
          <motion.section 
            className="mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gray-900/50 rounded-2xl p-8 md:p-12 border border-pink-500/30">
              <h2 className="text-3xl md:text-4xl font-bold text-pink-300 mb-8">Guidelines for Music & Audio Content</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                We welcome covers, remixes, and other musical derivative works featuring songs by our talents. 
                These guidelines apply specifically to musical content:
              </p>
              
              <div className="space-y-6">
                {musicGuidelines.map((guideline, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="bg-gray-800/50 rounded-xl p-6 border border-pink-500/30"
                  >
                    <h3 className="text-xl font-bold text-pink-300 mb-3">{guideline.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{guideline.description}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 bg-red-500/10 rounded-xl p-6 border border-red-500/30">
                <h3 className="text-xl font-bold text-red-300 mb-4">Prohibited Audio Use</h3>
                <p className="text-red-300 font-semibold">
                  ❌ Voice extraction or synthesis: Do not extract talent voices from songs for use in speech 
                  generation, AI voice synthesis, or similar applications. This is strictly prohibited.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Fan Art & Visual Content */}
          <motion.section 
            className="mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gray-900/50 rounded-2xl p-8 md:p-12 border border-green-500/30">
              <h2 className="text-3xl md:text-4xl font-bold text-green-300 mb-8">Fan Art & Visual Content Guidelines</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-black mb-4">Encouraged Creative Works</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Original fan art and illustrations</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Character redesigns and interpretations</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Memes and comedic content</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Cosplay photography and videos</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Animation and motion graphics</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-black mb-4">Content Standards</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Keep content family-friendly and respectful</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Avoid overly suggestive or inappropriate themes</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Respect the personality and image of our talents</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Credit Livneu when sharing</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 bg-purple-500/10 rounded-xl p-6 border border-purple-500/30">
                <h3 className="text-xl font-bold text-purple-300 mb-4">Official Use Permission</h3>
                <p className="text-gray-700 leading-relaxed">
                  Please note that we may use fan-created derivative works as stream thumbnails, social media posts, 
                  promotional materials, or other official content. By creating and sharing derivative works, you 
                  grant us permission for this usage with appropriate credit.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Commercial Use & Licensing */}
          <motion.section 
            className="mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gray-900/50 rounded-2xl p-8 md:p-12 border border-purple-500/30">
              <h2 className="text-3xl md:text-4xl font-bold text-purple-300 mb-8">Commercial Use & Special Permissions</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
                  <h3 className="text-xl font-bold text-red-300 mb-4">❌ Prohibited Commercial Activities</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-red-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Selling physical merchandise or products</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-red-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Commercial music distribution or sales</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-red-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Corporate-sponsored content without permission</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-red-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Using our IP for business promotion</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-green-500/10 rounded-xl p-6 border border-green-500/30">
                  <h3 className="text-xl font-bold text-green-300 mb-4">✅ Allowed Platform Monetization</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">YouTube Partner Program revenue</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Twitch affiliate/partner earnings</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Platform-integrated donations/tips</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-gray-700">Patreon or Ko-fi support for creators</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 bg-blue-500/10 rounded-xl p-6 border border-blue-500/30">
                <h3 className="text-xl font-bold text-blue-300 mb-4">Need Special Permission?</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For commercial licensing, corporate partnerships, large-scale projects, or activities outside 
                  these guidelines, please contact our business development team:
                </p>
                <p className="text-purple-400 font-semibold">levelstair@gmail.com</p>
              </div>
            </div>
          </motion.section>

          {/* Legal & Final Notes */}
          <motion.section 
            className="mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gray-900/50 rounded-2xl p-8 md:p-12 border border-gray-500/30">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-700 mb-8">Important Legal Notes</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-black mb-3">Copyright Retention</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Livneu does not relinquish copyright or related rights through these guidelines. 
                    We maintain all intellectual property rights to our original content and characters.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-black mb-3">No Guarantees</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We make no guarantees regarding the use of our content or derivative works, including 
                    potential conflicts with third-party rights. Users are responsible for ensuring their 
                    content complies with all applicable laws and regulations.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-black mb-3">Limitation of Liability</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Livneu shall not be responsible for disputes arising from the use, performance, 
                    or submission of derivative works, including conflicts with third parties.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-black mb-3">Guidelines Updates</h3>
                  <p className="text-gray-700 leading-relaxed">
                    These guidelines may be updated without notice. Please check this page regularly and 
                    ensure you follow the most current version. Continued use of our content constitutes 
                    acceptance of any updates.
                  </p>
                </div>
              </div>

              <div className="mt-8 bg-purple-500/10 rounded-xl p-6 border border-purple-500/30">
                <p className="text-purple-300 font-semibold text-center">
                  By creating derivative works using Livneu content, you acknowledge that you have 
                  read, understood, and agree to comply with these guidelines.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Contact Information */}
          <motion.section 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl p-8 md:p-12 border border-purple-500/30">
              <h2 className="text-3xl font-bold text-black text-center mb-8">Questions or Concerns?</h2>
              <p className="text-lg text-gray-700 leading-relaxed text-center mb-8">
                If you have questions about these guidelines or need clarification about specific use cases, 
                don't hesitate to reach out to us.
              </p>
              
              {/* <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <h3 className="text-lg font-bold text-purple-300 mb-2">General Guidelines</h3>
                  <p className="text-gray-700 text-sm">levelstair@gmail.com</p>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-bold text-pink-300 mb-2">Commercial Licensing</h3>
                  <p className="text-gray-700 text-sm">levelstair@gmail.com</p>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-bold text-purple-400 mb-2">Reporting Issues</h3>
                  <p className="text-gray-700 text-sm">levelstair@gmail.com</p>
                </div>
              </div> */}
            </div>
          </motion.section>

          </div>
        </section>
      </div>
    </HelmetProvider>
  );
};

export default DerivativeWorks;
